<script setup>
/* eslint-disable no-console */
import { ref, computed, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { informarVisita, xmlToJson } from 'dashboard/api/cea';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'reported']);

const dialogRef = ref(null);
const isSubmitting = ref(false);
const showContactSection = ref(false);

// Form state
const fechaVisita = ref('');
const resultado = ref('true');
const idOperario = ref('');
const nombreOperario = ref('');
const cifContratista = ref('');
const nombreContratista = ref('');
const codIncidencia = ref(0);
const descIncidencia = ref('Sin incidencia');
const observaciones = ref('');

// Contact fields (optional)
const personaNombre = ref('');
const personaApellido1 = ref('');
const personaApellido2 = ref('');
const personaTelefono = ref('');
const personaNif = ref('');

const nowDatetime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const resetForm = () => {
  fechaVisita.value = nowDatetime();
  resultado.value = 'true';
  idOperario.value = '';
  nombreOperario.value = '';
  cifContratista.value = '';
  nombreContratista.value = '';
  codIncidencia.value = 0;
  descIncidencia.value = 'Sin incidencia';
  observaciones.value = '';
  personaNombre.value = '';
  personaApellido1.value = '';
  personaApellido2.value = '';
  personaTelefono.value = '';
  personaNif.value = '';
  showContactSection.value = false;
};

const isFormValid = computed(() => {
  return (
    fechaVisita.value &&
    idOperario.value.trim() &&
    nombreOperario.value.trim() &&
    cifContratista.value.trim() &&
    nombreContratista.value.trim()
  );
});

const handleClose = () => {
  resetForm();
  emit('close');
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    const payload = {
      id: '',
      codOrden: props.order.aquasis_order_id,
      fechaVisita: new Date(fechaVisita.value).toISOString(),
      resultado: resultado.value,
      idOperario: idOperario.value,
      nombreOperario: nombreOperario.value,
      cifContratista: cifContratista.value,
      nombreContratista: nombreContratista.value,
      codIncidencia: String(codIncidencia.value),
      descIncidencia: descIncidencia.value,
      observaciones: (observaciones.value || '').slice(0, 120),
      codVinculacion: '',
      idDocFirma: '',
      personaNombre: personaNombre.value,
      personaApellido1: personaApellido1.value,
      personaApellido2: personaApellido2.value,
      personaTelefono: personaTelefono.value,
      personaNif: personaNif.value,
    };

    const xmlDoc = await informarVisita(payload);

    const returnEl =
      xmlDoc.getElementsByTagName('return')[0] ||
      xmlDoc.getElementsByTagName('informarVisitaReturn')[0] ||
      xmlDoc.getElementsByTagName('informarVisitaResponse')[0];

    let visitId = null;
    if (returnEl) {
      if (returnEl.children && returnEl.children.length > 0) {
        const json = xmlToJson(returnEl);
        visitId = json?.idVisita || json?.id || returnEl.textContent?.trim();
      } else {
        visitId = returnEl.textContent?.trim();
      }
    }

    const msg = visitId
      ? `Visita registrada exitosamente (ID: ${visitId})`
      : 'Visita registrada exitosamente';
    useAlert(msg);
    emit('reported');
    handleClose();
  } catch (error) {
    console.error('Error reporting visit:', error);
    useAlert('Error al registrar la visita');
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      resetForm();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    width="md"
    title="Informar Visita"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="isSubmitting ? 'Registrando...' : 'Registrar Visita'"
    :disable-confirm-button="!isFormValid || isSubmitting"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Order reference -->
      <div class="bg-n-solid-3 rounded-lg p-3 border border-n-weak">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-11">Orden Aquasis</span>
          <span class="font-mono text-sm font-semibold text-n-brand">
            {{ order.aquasis_order_id }}
          </span>
        </div>
      </div>

      <!-- Visit Info Section -->
      <h4 class="text-sm font-semibold text-n-slate-12">
        Informacion de la Visita
      </h4>

      <!-- Fecha de Visita -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Fecha de Visita <span class="text-n-ruby-11">*</span>
        </label>
        <input
          v-model="fechaVisita"
          type="datetime-local"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>

      <!-- Resultado -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Resultado <span class="text-n-ruby-11">*</span>
        </label>
        <select
          v-model="resultado"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option value="true">Exitosa</option>
          <option value="false">No exitosa</option>
        </select>
      </div>

      <!-- Operario -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            ID Operario <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="idOperario"
            type="text"
            placeholder="ID del operario"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Nombre Operario <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="nombreOperario"
            type="text"
            placeholder="Nombre del operario"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Contratista -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            CIF Contratista <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="cifContratista"
            type="text"
            placeholder="CIF del contratista"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Nombre Contratista <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="nombreContratista"
            type="text"
            placeholder="Nombre del contratista"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Incidencia -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Codigo Incidencia <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model.number="codIncidencia"
            type="number"
            min="0"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Descripcion Incidencia <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="descIncidencia"
            type="text"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Observaciones -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">Observaciones</label>
        <textarea
          v-model="observaciones"
          rows="3"
          placeholder="Observaciones adicionales..."
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
        />
      </div>

      <!-- Collapsible Contact Section -->
      <div class="border border-n-weak rounded-lg">
        <button
          type="button"
          class="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg"
          @click="showContactSection = !showContactSection"
        >
          <span>Contacto Responsable (opcional)</span>
          <span
            class="transition-transform"
            :class="showContactSection ? 'rotate-180' : ''"
          >
            <span class="i-lucide-chevron-down text-sm" />
          </span>
        </button>
        <div v-if="showContactSection" class="px-3 pb-3 flex flex-col gap-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">Nombre</label>
              <input
                v-model="personaNombre"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">Apellido 1</label>
              <input
                v-model="personaApellido1"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">Apellido 2</label>
              <input
                v-model="personaApellido2"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">Telefono</label>
              <input
                v-model="personaTelefono"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">NIF</label>
              <input
                v-model="personaNif"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-10">
        <span class="text-n-ruby-11">*</span> Campos requeridos
      </div>
    </div>
  </Dialog>
</template>
